import api, { getAxiosBaseUrl } from "./axios";

export class ApiError extends Error {
  constructor(message, status, data, request = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.code = data?.code || "";
    this.details = data?.details || null;
    this.requestId = data?.requestId || "";
    this.method = String(request?.method || "").toUpperCase();
    this.path = String(request?.path || "");
  }
}

function normalizeAxiosError(error) {
  const status = error?.response?.status || 0;
  const data = error?.response?.data || null;
  const method = String(error?.config?.method || "").toUpperCase();
  const path = String(error?.config?.url || "");

  const message =
    data?.message ||
    data?.error ||
    error?.message ||
    "Error de conexión con el servidor";

  return new ApiError(message, status, data, { method, path });
}

function apiBasePath() {
  const rawBase = String(getAxiosBaseUrl() || "").trim();
  if (!rawBase) return "";

  try {
    const parsed = new URL(
      rawBase,
      typeof window !== "undefined" ? window.location.origin : "http://localhost",
    );
    return parsed.pathname.replace(/\/+$/, "") || "/";
  } catch {
    return rawBase
      .replace(/[?#].*$/, "")
      .replace(/^https?:\/\/[^/]+/i, "")
      .replace(/\/+$/, "") || "/";
  }
}

export function resolveApiContractPath(contractPath) {
  const normalized = `/${String(contractPath || "").trim().replace(/^\/+/, "")}`;
  if (!normalized.startsWith("/api/") && normalized !== "/api") {
    throw new TypeError(`Contrato API inválido: ${normalized}`);
  }

  // Axios combina baseURL y url incluso cuando url comienza con "/". Cuando
  // la base ya termina en /api se quita sólo ese prefijo para evitar /api/api.
  return /\/api$/i.test(apiBasePath())
    ? normalized.replace(/^\/api(?=\/|$)/i, "") || "/"
    : normalized;
}

export async function apiRequest(path, options = {}) {
  try {
    const {
      method = "GET",
      body,
      headers,
      params,
      signal,
      ...restOptions
    } = options;

    const response = await api.request({
      url: path,
      method,
      data: body,
      headers,
      params,
      signal,
      ...restOptions,
    });

    return response.data;
  } catch (error) {
    const normalizedError = normalizeAxiosError(error);

    if (
      normalizedError.status === 402 &&
      normalizedError.data?.code === "BILLING_RESTRICTED" &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("webprofit:billing-restricted", {
          detail: {
            path,
            access: normalizedError.data?.access || {},
          },
        }),
      );
    }

    throw normalizedError;
  }
}

export function apiContractRequest(contractPath, options = {}) {
  return apiRequest(resolveApiContractPath(contractPath), options);
}

export async function apiRequestFirst(paths, options = {}) {
  const list = Array.isArray(paths) ? paths : [paths];
  let lastError = null;

  for (const path of list) {
    try {
      return await apiRequest(path, options);
    } catch (error) {
      lastError = error;

      // Solo prueba la siguiente ruta cuando la actual no existe.
      if (error?.status !== 404) {
        throw error;
      }
    }
  }

  throw lastError || new ApiError("Ruta no encontrada", 404, null);
}

export function formatApiError(error, fallback = "No se pudo completar la operación") {
  const baseMessage = String(error?.message || fallback).trim() || fallback;
  const fieldMessages = Array.isArray(error?.details?.fields)
    ? error.details.fields
        .slice(0, 3)
        .map((field) => String(field?.message || "").trim())
        .filter(Boolean)
    : [];
  const detail = fieldMessages.length ? ` ${fieldMessages.join(" ")}` : "";
  const code = String(error?.code || "").trim();
  const requestId = String(error?.requestId || "").trim();
  const references = [
    code && code !== "NOT_FOUND" ? code : "",
    requestId ? `solicitud ${requestId}` : "",
  ].filter(Boolean);

  return `${baseMessage}${detail}${references.length ? ` (${references.join(" · ")})` : ""}`;
}

export function getApiBaseUrl() {
  return getAxiosBaseUrl();
}
